// "Saisir un congé" / "Poser une récup" — dual mini-month range picker
// with live balance projection (proto CgSaisieModal). Submits through
// POST /leaves/consume; the working-day count shown here is a client
// preview (ven/sam + fériés), the server recomputes authoritatively.
import { useState } from 'react';
import { toast } from '../../store/toast.js';
import { useConsumeLeave, useLeaveBalances, type LeaveType } from '../../api/hooks.js';
import {
  MiniMonth, useHolidayMap, countWorkdays, cgRange, cgWeekend,
  cgAvColor, cgInitials, cgNum, cgFmtLong, cgFmtRange, cgToday, entityById, shiftYm,
} from './common.js';

export interface SaisiePreset { empId?: string | null; date?: string | null }

export function SaisieModal({ type, entityId, preset, onClose }: {
  type: LeaveType;
  entityId: string;
  preset: SaisiePreset;
  onClose: () => void;
}) {
  const balances = useLeaveBalances({ type, entityId });
  const consume = useConsumeLeave();
  const list = balances.data ?? [];

  const [empId, setEmpId] = useState<string | null>(preset.empId ?? null);
  const [sel, setSel] = useState<{ from: string | null; to: string | null }>({ from: preset.date ?? null, to: preset.date ?? null });
  const [hoverEnd, setHoverEnd] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [m0, setM0] = useState((preset.date ?? cgToday()).slice(0, 7));
  const [q, setQ] = useState('');
  const m1 = shiftYm(m0, 1);
  const holidays = useHolidayMap(m0, m1);

  const filtered = list.filter((r) => !q || r.employee.name.toLowerCase().includes(q.toLowerCase()));
  const row = list.find((r) => r.employee.id === (empId ?? filtered[0]?.employee.id));
  const effEmp = row?.employee ?? null;
  const stock = row?.balance.current ?? 0;

  const lo = sel.from && sel.to ? (sel.from < sel.to ? sel.from : sel.to) : sel.from;
  const hi = sel.from && sel.to ? (sel.from < sel.to ? sel.to : sel.from) : sel.from;
  const days = lo && hi ? countWorkdays(lo, hi, holidays) : 0;
  const excluded = lo && hi ? cgRange(lo, hi).filter((d) => cgWeekend(d) || holidays.has(d)).length : 0;
  const after = stock - days;
  const over = after < 0;

  const pick = (d: string) =>
    setSel((s) => (!s.from || (s.from && s.to)) ? { from: d, to: null } : { from: d < s.from ? d : s.from, to: d < s.from ? s.from : d });

  const submit = async () => {
    if (!effEmp || !lo || !hi || !days) return;
    try {
      const r = await consume.mutateAsync({ employeeId: effEmp.id, type, startDate: lo, endDate: hi, reason: note || undefined });
      toast.success(`${type === 'CONGE' ? 'Congé' : 'Récup'} enregistré pour ${effEmp.name} · ${r.days} j · solde ${cgNum(r.current)} j`);
      onClose();
    } catch (e) {
      const b = (e as Error & { body?: { error?: string } }).body;
      toast.danger(b?.error === 'insufficient_balance' ? 'Solde insuffisant.'
        : b?.error === 'no_working_days' ? 'Aucun jour ouvrable sélectionné.' : 'Échec de la saisie.');
    }
  };

  return (
    <div className="cg-veil center" onClick={onClose}>
      <div className="cg-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="cg-mo-head">
          <div>
            <div className="sb-mo-title">{type === 'CONGE' ? 'Saisir un congé' : 'Poser une récup'}</div>
            <div className="sb-mo-sub">Débit immédiat du solde · week-ends et jours fériés exclus du décompte.</div>
          </div>
          <button className="sb-dr-x" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="cg-mo-grid">
          <div className="cg-mo-left">
            <label className="sb-dr-lbl">Employé</label>
            <input className="cg-search" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="cg-emplist">
              {filtered.map((r) => {
                const ent = entityById(r.employee.entityId);
                const on = r.employee.id === effEmp?.id;
                return (
                  <button key={r.employee.id} type="button" className={'cg-empopt' + (on ? ' on' : '')} onClick={() => setEmpId(r.employee.id)}>
                    <span className="gm-av sm" style={{ background: cgAvColor(r.employee.id) }}>{cgInitials(r.employee.name)}</span>
                    <span className="cg-empopt-m">
                      <b>{r.employee.name}</b>
                      <i>{r.employee.role ?? r.employee.code}{ent ? ` · ${ent.short}` : ''}</i>
                    </span>
                    <span className="cg-empopt-b gm-mono">{cgNum(r.balance.current)} j</span>
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="cg-empty sm">Aucun employé.</div>}
            </div>
          </div>
          <div className="cg-mo-right">
            <div className="cg-mo-calhead">
              <button className="gm-mn-arrow" onClick={() => setM0(shiftYm(m0, -1))}>‹</button>
              <span className="cg-mo-calrange">
                {sel.from ? (sel.to ? cgFmtRange(lo!, hi!) : cgFmtLong(sel.from) + ' → …') : 'Sélectionnez une date de début'}
              </span>
              <button className="gm-mn-arrow" onClick={() => setM0(shiftYm(m0, 1))}>›</button>
            </div>
            <div className="cg-mm-pair" onMouseLeave={() => setHoverEnd(null)}>
              <MiniMonth month={m0} sel={sel} hoverEnd={sel.from && !sel.to ? hoverEnd : null} onPick={pick} onHover={setHoverEnd} holidays={holidays} />
              <MiniMonth month={m1} sel={sel} hoverEnd={sel.from && !sel.to ? hoverEnd : null} onPick={pick} onHover={setHoverEnd} holidays={holidays} />
            </div>
            <input className="cg-search wide" placeholder="Note (facultatif) — motif, remplaçant, contact…" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className={'cg-proj' + (over ? ' over' : '')}>
              <div className="cg-proj-l">
                <b className="gm-mono">{days}</b> jour{days > 1 ? 's' : ''} ouvrable{days > 1 ? 's' : ''}
                {excluded > 0 && <i> · {excluded} jour{excluded > 1 ? 's' : ''} non décompté{excluded > 1 ? 's' : ''}</i>}
              </div>
              <div className="cg-proj-r">
                <span>{type === 'RECUP' ? 'Stock récup' : 'Solde'} après</span>
                <b className="gm-mono">{cgNum(stock)}</b><span className="cg-arrow">→</span>
                <b className={'gm-mono ' + (over ? 'bad' : 'ok')}>{cgNum(after)} j</b>
              </div>
            </div>
            {over && <div className="cg-mo-warn">Solde insuffisant — la saisie sera refusée par le serveur ({cgNum(-after)} j manquants).</div>}
          </div>
        </div>
        <div className="cg-mo-foot">
          <button className="cg-ghost" onClick={onClose}>Annuler</button>
          <button className="gm-attn-go" disabled={!days || over || consume.isPending} onClick={submit}>
            Enregistrer {days ? `· ${days} j` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
